import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Bacs, BacsDTO } from '../models/workflows/bacs';
import { Observable } from 'rxjs';
import { BacsConfiguration } from '../models/workflows/bacs-configuration';
import { BacsConditions } from '../models/workflows/bacs-conditions';
import { BacsConditionForm } from '../models/workflows/bacs-condition-form';
import { WorkflowsRightsUsersForm } from '../models/workflows/rights-users-form';
import { WorkflowsRightsServicesForm } from '../models/workflows/rights-services-form';
import { WorkflowsRightsGroupsForm } from '../models/workflows/rights-groups-form';
import { BacSearch } from '../models/workflows/bac-search';
import { BacForm } from '../models/workflows/bac-form';
import { BacGetForm, BacSearchForm } from '../models/workflows/bac-search-form';
import { WorkflowDefinitionsSearchForm } from '../models/workflows/workflow-definition-search-form';
import { WorkflowDefinitionsPaginate, WorkflowDefinitionsVersion } from '../models/workflows/workflow-definition';
import { WorkflowDefinitionsForm } from '../models/workflows/workflow-definition-form';
import { WorkflowInstanceDTO } from '../models/workflows/workflow-instance';
import { WorkflowsRightMails } from '../models/workflows/right-mails';
import { WorkflowResponseTypeActionDTO } from 'src/app/shared/models/workflows/actions';
import { environment } from 'src/environments/environment';
import { StartWorkflowManually } from 'src/app/shared/models/workflows/start-worflow-manually';

@Injectable({
  providedIn: 'root',
})
export class WorkflowsService {
  public apiBaseUrl: string;
  public apiICourrierBaseUrl = '';
  public body: FormData;
  public userData;
  public headers: HttpHeaders;
  public params: HttpParams = new HttpParams();

  constructor(public http: HttpClient) {
    const config = environment;
    this.apiBaseUrl = config.apiICourrierWorkflowsBaseUrl;
    this.apiICourrierBaseUrl = config.apiICourrierBaseUrl;
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    this.body = new FormData();
    this.params = new HttpParams();
  }

  // #region Bacs Management
  getBacs(data?: Partial<BacGetForm>): Observable<BacsDTO[]> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => (params = params.set(key, data[key])));
    }
    return this.http.get<BacsDTO[]>(`${this.apiICourrierBaseUrl}/Bacs?${params}`);
  }
  getBacsSearch(data?: Partial<BacSearchForm>): Observable<BacSearch[]> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => (params = params.set(key, data[key])));
    }

    return this.http.get<BacSearch[]>(`${this.apiICourrierBaseUrl}/Bacs/Me/Search?${params}`);
  }
  //#endregion

  //#region move to bac
  moveToBac(id: number, bacIdToMove: number): Observable<void> {
    return this.http.patch<void>(`${this.apiICourrierBaseUrl}/Mails/${id}/MoveToBac?bacId=${bacIdToMove}`, null);
  }
  //#endregion

  // #region Flux / WorkflowDefinitions
  getFlux(data?: Partial<WorkflowDefinitionsSearchForm>): Observable<WorkflowDefinitionsPaginate> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => {
        if (data[key] !== null) {
          params = params.set(key, data[key]);
        }
      });
    }
    return this.http.get<WorkflowDefinitionsPaginate>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/Search?${params}`);
  }

  getFluxByIdVersionId(id: number, versionID: number): Observable<WorkflowDefinitionsVersion> {
    return this.http.get<WorkflowDefinitionsVersion>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/${id}/Versions/${versionID}`);
  }

  deleteFluxById(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/${id}`);
  }

  postFlux(data: WorkflowDefinitionsForm): Observable<WorkflowDefinitionsVersion> {
    return this.http.post<WorkflowDefinitionsVersion>(`${this.apiBaseUrl}/Management/WorkflowDefinitions`, data);
  }
  //#endregion

  // #region Flux Instance
  getWorkflowId(id: number, workFlowId: string): Observable<WorkflowInstanceDTO> {
    return this.http.get<WorkflowInstanceDTO>(`${this.apiBaseUrl}/Mails/${id}/WorkflowInstances/${workFlowId}`);
  }
  //#endregion

  // #region Flux Rights
  getWorkflowIdRight(id: number): Observable<WorkflowsRightMails> {
    return this.http.get<WorkflowsRightMails>(`${this.apiBaseUrl}/Me/Rights/Mails/${id}`);
  }
  //#endregion

  // #region Flux Actions
  resumeFlow(id: number, workflowID: string): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/Mails/${id}/WorkflowInstances/${workflowID}/Resume`, null);
  }

  retryFaultedFlow(id: number, workflowID: string): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/Mails/${id}/WorkflowInstances/${workflowID}/RetryFaulted`, null);
  }

  cancelFlow(id: number, workflowID: string): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/Mails/${id}/WorkflowInstances/${workflowID}/Cancel`, null);
  }

  deleteFlow(id: number, workflowID: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/Mails/${id}/WorkflowInstances/${workflowID}`);
  }

  publishFlux(id: number): Observable<WorkflowDefinitionsVersion> {
    return this.http.post<WorkflowDefinitionsVersion>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/${id}/Publish`, null);
  }

  unPublishFlux(id: number): Observable<WorkflowDefinitionsVersion> {
    return this.http.post<WorkflowDefinitionsVersion>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/${id}/Unpublish`, null);
  }

  startFlowManually(id: number): Observable<StartWorkflowManually> {
    return this.http.post<StartWorkflowManually>(`${this.apiBaseUrl}/Mails/${id}/StartWorkflowManually`, null);
  }

  getAvailableBacsSources(): Observable<Bacs[]> {
    return this.http.get<Bacs[]>(`${this.apiBaseUrl}/Management/WorkflowDefinitions/AvailableBacSources`);
  }
  //#endregion

  // #region Mail Actions for Flow
  getWorkflowActionResponse(mailId: number, actionId: number): Observable<WorkflowResponseTypeActionDTO> {
    return this.http.get<WorkflowResponseTypeActionDTO>(`${this.apiBaseUrl}/Mails/${mailId}/Actions/${actionId}/ResponseTypeInfos`);
  }
  //#endregion
}
